import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const CallStatusWidget = () => {
  const [callStatus, setCallStatus] = useState('idle'); // idle, active, incoming, on-hold
  const [callDuration, setCallDuration] = useState(0);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [isMinimized, setIsMinimized] = useState(false);

  // Simulate active call data
  const activeCallData = {
    customerName: 'John Anderson',
    customerPhone: '+1 (555) 123-4567',
    callType: 'Inbound',
    priority: 'High'
  };

  // Timer for call duration
  useEffect(() => {
    let interval;
    if (callStatus === 'active') {
      interval = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    } else {
      setCallDuration(0);
    }

    return () => clearInterval(interval);
  }, [callStatus]);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins?.toString()?.padStart(2, '0')}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const handleAnswerCall = () => {
    setCallStatus('active');
    setCustomerInfo(activeCallData);
  };

  const handleEndCall = () => {
    setCallStatus('idle');
    setCustomerInfo(null);
    setCallDuration(0);
  };

  const handleHoldCall = () => {
    setCallStatus(callStatus === 'on-hold' ? 'active' : 'on-hold');
  };

  const simulateIncomingCall = () => {
    setCallStatus('incoming');
    setCustomerInfo(activeCallData);
  };

  // Don't render widget when no call activity
  if (callStatus === 'idle') {
    return (
      <div className="fixed top-4 right-80 z-1010">
        <Button
          variant="outline"
          size="sm"
          onClick={simulateIncomingCall}
          className="bg-white shadow-subtle"
        >
          <Icon name="Phone" size={16} className="mr-2" />
          Simulate Call
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed top-4 right-80 z-1010">
      <div className={`call-widget ${callStatus === 'active' ? 'call-active' : ''} ${
        isMinimized ? 'w-48' : 'w-80'
      } transition-all duration-300 ease-smooth`}>
        
        {/* Widget Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <div className={`w-3 h-3 rounded-full ${
              callStatus === 'active' ? 'bg-success pulse-slow' :
              callStatus === 'incoming' ? 'bg-warning pulse-slow' :
              callStatus === 'on-hold' ? 'bg-warning' : 'bg-slate-400'
            }`} />
            <span className="text-sm font-medium text-slate-900">
              {callStatus === 'active' ? 'Active Call' :
               callStatus === 'incoming' ? 'Incoming Call' :
               callStatus === 'on-hold' ? 'Call On Hold' : 'Call'}
            </span>
          </div>
          
          <div className="flex items-center space-x-1">
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-1 text-slate-500 hover:text-slate-700 micro-hover focus-ring rounded"
            >
              <Icon name={isMinimized ? "Maximize2" : "Minimize2"} size={14} />
            </button>
          </div>
        </div>

        {/* Call Timer */}
        {(callStatus === 'active' || callStatus === 'on-hold') && (
          <div className="mb-3">
            <div className="call-timer text-lg font-semibold text-slate-900">
              {formatDuration(callDuration)}
            </div>
          </div>
        )}

        {/* Customer Information */}
        {!isMinimized && customerInfo && (
          <div className="mb-4 p-3 bg-slate-50 rounded-lg">
            <div className="text-sm font-medium text-slate-900 mb-1">
              {customerInfo?.customerName}
            </div>
            <div className="text-xs text-slate-600 mb-2">
              {customerInfo?.customerPhone}
            </div>
            <div className="flex items-center space-x-2">
              <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                customerInfo?.callType === 'Inbound' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
              }`}>
                <Icon name={customerInfo?.callType === 'Inbound' ? "ArrowDown" : "ArrowUp"} size={10} className="mr-1" />
                {customerInfo?.callType}
              </span>
              {customerInfo?.priority && (
                <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                  customerInfo?.priority === 'High' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {customerInfo?.priority}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Call Controls */}
        <div className="flex items-center justify-between space-x-2">
          {callStatus === 'incoming' && (
            <>
              <Button
                variant="success"
                size="sm"
                onClick={handleAnswerCall}
                className="flex-1"
                iconName="Phone"
                iconPosition="left"
              >
                Answer
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleEndCall}
                iconName="PhoneOff"
              />
            </>
          )}

          {(callStatus === 'active' || callStatus === 'on-hold') && (
            <>
              {!isMinimized && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleHoldCall}
                  iconName={callStatus === 'on-hold' ? "Play" : "Pause"}
                >
                  {callStatus === 'on-hold' ? 'Resume' : 'Hold'}
                </Button>
              )}
              
              <Button
                variant="destructive"
                size={isMinimized ? "sm" : "sm"}
                onClick={handleEndCall}
                iconName="PhoneOff"
                className={isMinimized ? "flex-1" : ""}
              >
                {isMinimized ? 'End' : 'End Call'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CallStatusWidget;